import React from 'react';
import { Navigate } from 'react-router-dom';
import { useUserRole } from '../hooks/useUserRole';
import { logger } from '../utils/logger';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'admin' | 'customer';
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole }) => {
  const { user, role, loading } = useUserRole();

  logger.log('🛡️ ProtectedRoute check:', { 
    user: user?.email, 
    role, 
    loading, 
    requiredRole 
  });

  if (loading) {
    logger.log('⏳ Loading user role...');
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!user) {
    logger.log('❌ No user, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  if (requiredRole && role !== requiredRole) {
    logger.log('🔄 Role mismatch, redirecting:', { 
      requiredRole, 
      actualRole: role 
    });
    // Redirect based on role
    if (role === 'admin') {
      logger.log('👨‍💼 Redirecting admin to admin dashboard');
      return <Navigate to="/admin-dashboard" replace />;
    } else {
      logger.log('👤 Redirecting customer to customer dashboard');
      return <Navigate to="/customer-dashboard" replace />;
    }
  }

  logger.log('✅ Access granted to:', requiredRole || 'any role');
  return <>{children}</>;
};

export default ProtectedRoute;
